// MorningBriefing — the ONE centred message that replaces every Orders banner, bar and corner card.
// Opens by itself once a day for people authorised for Orders (useBriefingGate), and again from the bell.
// Renders through a portal and carries its own scoped tokens (.briefRoot) — never :root.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNotifications } from '../../../hooks/useNotifications';
import { openNotification } from '../../../utils/entityRoutes';
import { buildBriefing, greeting, verdict, dayKey } from './briefingModel';
import { useBriefingStore, getSnoozed, setSnoozed, setRemind, activeSnoozed } from './briefingStore';
import { useBriefingGate } from './useBriefingGate';
import { Ring, Week, Section } from './BriefingParts';
import { ItemRow } from './BriefingRow';
import BriefingZoom from './BriefingZoom';
import { IcX, IcClock, IcCheck } from './briefingIcons';
import useModelPhotos from '../../../hooks/useModelPhotos';
import styles from './briefing.module.css';

export function MorningBriefing() {
  const { name, ordersAllowed, userId } = useBriefingGate();
  const { markRead } = useNotifications();
  const { open, snapshot, handled, hide, handle, unhandle, reset } = useBriefingStore();
  const handledSet = useMemo(() => new Set(handled), [handled]);
  const now = useMemo(() => new Date(), [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const b = useMemo(() => buildBriefing(snapshot, now, handledSet), [snapshot, now, handledSet]);
  const bodyRef = useRef(null);
  const cardRef = useRef(null);
  const focusRef = useRef([]); focusRef.current = b.focus;
  const actionsRef = useRef({});
  const [big, setBig] = useState(null);                       // the photo shown bigger: { item, photo }
  const bigRef = useRef(null); bigRef.current = big;
  const openerRef = useRef(null);                            // the picture button that opened it: focus goes back there
  const closeBig = () => {
    setBig(null);
    const el = openerRef.current; openerRef.current = null;
    const back = el && el.isConnected ? el : cardRef.current;
    if (back && back.focus) back.focus();
  };
  const closeBigRef = useRef(closeBig); closeBigRef.current = closeBig;
  // The model pictures: the same read as the Orders Models screen; only while the briefing is open.
  const models = useMemo(() => (open ? b.items.map((i) => i.model).filter(Boolean) : []), [open, b.items]);
  const photos = useModelPhotos(models);

  // Closing is the moment the "Done" items are marked read: until then every one can be undone.
  const close = () => {
    const snoozed = activeSnoozed(dayKey(new Date()));   // snoozed = left unread on purpose, until tomorrow
    useBriefingStore.getState().handled.forEach((id) => { if (!snoozed[id]) markRead(id); });   // live list: includes an item handled a tick ago (Open)
    hide();
  };
  const closeRef = useRef(close); closeRef.current = close;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target; const typing = t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable);
      if (typing) return;                                   // never steal a key from a field
      const swallow = () => { e.preventDefault(); e.stopPropagation(); };
      if (bigRef.current) {                                    // the big photo takes EVERY key: nothing reaches the briefing or the app behind it
        if (e.key === 'Tab') return;
        swallow();
        if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') closeBigRef.current();
        return;
      }
      if (e.key === 'Escape') { swallow(); closeRef.current(); }
      else if (e.key === 'Enter' && !(t && t.closest && t.closest('button'))) { swallow(); closeRef.current(); }
      else if (/^[1-3]$/.test(e.key)) { swallow(); const f = focusRef.current[+e.key - 1]; if (f) actionsRef.current.open(f); }
      else if (['/', 'n', 'N', '?'].includes(e.key)) swallow();   // the app's own shortcuts stay quiet behind the modal
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  useEffect(() => { if (open && cardRef.current) cardRef.current.focus(); }, [open]);

  useEffect(() => { if (!ordersAllowed) reset(); }, [ordersAllowed, reset]);
  useEffect(() => { if (!open) setBig(null); }, [open]);
  if (!open || !ordersAllowed) return null;

  const actions = {
    open: (i) => { if (openNotification(i.n)) { handle(i.id); closeRef.current(); } },
    zoom: (item, photo) => { openerRef.current = document.activeElement; setBig({ item, photo }); },
    done: (i) => handle(i.id),
    undo: (i) => { const m = getSnoozed(); if (m[i.id]) { delete m[i.id]; setSnoozed(m); } unhandle(i.id); },
    snooze: (i) => {
      const t = new Date(now); t.setDate(t.getDate() + 1);
      setSnoozed({ ...getSnoozed(), [i.id]: dayKey(t) }); handle(i.id);
    },
  };
  actionsRef.current = actions;
  const remindAt14 = () => {
    const t = new Date(); t.setHours(14, 0, 0, 0);
    if (t.getTime() <= Date.now()) t.setDate(t.getDate() + 1);
    setRemind(userId, t.getTime()); close();
  };
  const jump = (k) => { const el = bodyRef.current && bodyRef.current.querySelector(`[data-test="briefing-sec-${k}"]`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
  const allDone = b.total > 0 && b.done === b.total;

  return createPortal(
    <div className={styles.briefRoot} data-test="morning-briefing">
      <div className={styles.scrim} onClick={close} />
      <div className={styles.card} ref={cardRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Morning briefing">
        <span className={styles.grab} aria-hidden="true" />
        <header className={styles.head}>
          <div className={styles.hTop}>
            <span className={styles.eyebrow}>Morning briefing · {now.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
            <span className={styles.hSub}>Orders team · once a day</span>
          </div>
          <button type="button" className={styles.x} onClick={close} aria-label="Close" data-test="briefing-close"><IcX /></button>
          <div className={styles.hMain}>
            <div>
              <h2 className={styles.hello}>{greeting(now, name)}</h2>
              <div className={styles.verdict} data-test="briefing-verdict">
                <span className={`${styles.vNum}${b.counts.needs ? '' : ' ' + styles.ok}`}>{b.counts.needs || '✓'}</span>
                <span className={styles.vText}>{verdict(b.counts).replace(/^\d+ /, '')}</span>
                <span className={styles.br} />
                <span className={`${styles.hChip} ${styles.t_soon}`}><span className={styles.dot} /><b>{b.counts.wait}</b>can wait</span>
                <span className={`${styles.hChip} ${styles.t_fyi}`}><span className={styles.dot} /><b>{b.counts.fyi}</b>just FYI</span>
              </div>
            </div>
            {b.total ? <Ring done={b.done} total={b.total} /> : null}
          </div>
          {b.total ? <Week week={b.week} todayKey={dayKey(now)} onLate={() => jump('late')} /> : null}
        </header>
        <div className={styles.body} ref={bodyRef}>
          {allDone || b.empty || (!b.counts.needs && !b.counts.wait) ? (
            <div className={styles.clear}><span className={styles.clearIc}><IcCheck /></span><b>All clear</b><span>{b.empty ? 'Nothing from Orders this morning.' : 'You handled everything. Nice start.'}</span></div>
          ) : null}
          {b.focus.length ? (
            <div className={styles.focus}>
              <div className={styles.K}>
                <span className={styles.k1}>Today’s focus</span><span className={styles.kc}>Model · PI</span><span className={styles.kc}>Supplier</span><span className={styles.kc}>Status</span>
                <span className={styles.k5}><kbd>1</kbd><kbd>2</kbd><kbd>3</kbd><span>to open</span></span>
              </div>
              {b.focus.map((i, n) => (
                <ItemRow key={i.id} item={i} rank={n + 1} handled={handledSet.has(i.id)} photo={photos.get(i.model)}
                  onOpen={() => actions.open(i)} onDone={() => actions.done(i)} onSnooze={() => actions.snooze(i)} onUndo={() => actions.undo(i)} onZoom={actions.zoom} />
              ))}
            </div>
          ) : null}
          {b.sections.map((s) => <Section key={s.k} s={s} handledSet={handledSet} actions={actions} photos={photos} />)}
        </div>
        <footer className={styles.foot}>
          <span className={styles.keys}><kbd>1</kbd><kbd>2</kbd><kbd>3</kbd><span>open</span><kbd>Enter</kbd><span>start</span><kbd>Esc</kbd><span>close</span></span>
          <button type="button" className={styles.ghost} onClick={remindAt14} data-test="briefing-remind" aria-label="Remind me at 14:00"><IcClock /><span className={styles.l}>Remind me at 14:00</span><span className={styles.s}>14:00</span></button>
          <button type="button" className={styles.primary} onClick={close} data-test="briefing-start">Got it, start my day <kbd>Enter</kbd></button>
        </footer>
      </div>
      {big ? <BriefingZoom item={big.item} photo={big.photo} onClose={closeBig} /> : null}
    </div>,
    document.body,
  );
}

export default MorningBriefing;
